import { useAddAdvancedSearch } from "@/api/advanced-search";
import { advancedSearchToSubmittable } from "@/config/forms/advanced-search/advanced-search-fields-def";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { toast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { FormField } from "../ui/form";
import { FormCol } from "../ui/form-col";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { AdvancedSearchFormData } from "./AdvancedSearchForm";

export const SaveAdvancedSearchModal = ({
  resource,
  query,
  open,
  onOpenChange,
}: {
  resource: string;
  query: AdvancedSearchFormData;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const form = useForm<{
    name: string;
    shared: boolean;
  }>({ defaultValues: { name: "", shared: false } });

  const { handleSubmit, formState, control } = form;

  const { t } = useTranslation();
  const { setAdvancedSearchQuery } = useAdvancedSearch();

  const addAdvancedSearchMutation = useAddAdvancedSearch();

  function onSubmit(values: { name: string; shared: boolean }) {
    addAdvancedSearchMutation.mutate(
      {
        resource,
        create: {
          ...values,
          query: {
            searchMode: query.searchMode,
            fields: advancedSearchToSubmittable(query.fields),
            version: "1",
          },
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: t("advancedSearchSaveSucessMessage"),
            duration: 1000,
            variant: "success",
          });

          setAdvancedSearchQuery({
            ...query,
            name: form.getValues("name"),
          });
          form.reset();
          if (onOpenChange) onOpenChange(false);
        },
        onError: (e) => {
          console.error("error when saving the AdvancedSearch :", e);
          toast({
            title: "Error",
            description: t("advancedSearchSaveErrorMessage"),
            duration: 1000,
            variant: "error",
          });
        },
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} modal>
      <DialogContent className="sm:max-w-fit">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-8 flex flex-col gap-8">
            <DialogHeader>
              <DialogTitle className="text-center">{t("save")}</DialogTitle>
            </DialogHeader>
            <FormCol className="gap-5">
              <FormField
                control={control}
                name="name"
                rules={{ required: true, minLength: 1 }}
                render={({ field }) => (
                  <Input
                    label={t("savedAdvancedSearchName")}
                    id="name"
                    {...field}
                  />
                )}
              />
              <div className="flex items-center space-x-2">
                <FormField
                  control={control}
                  name="shared"
                  render={({ field }) => (
                    <>
                      <Switch
                        id="shared"
                        onCheckedChange={field.onChange}
                        checked={field.value}
                      />
                      <Label htmlFor="shared">{t("shared")}</Label>
                    </>
                  )}
                />
              </div>
            </FormCol>
          </div>
          <DialogFooter className="sm:justify-around">
            <DialogClose asChild>
              <Button className="w-full" type="button" variant={"secondary"}>
                {t("advancedSearchCancelSave")}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={!formState.isValid}
              className="w-full"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
